import React, { useState } from 'react';
import {
  Avatar as MuiAvatar,
  AvatarProps as MuiAvatarProps,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  Typography,
  Badge,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BedSharp, Person } from '@mui/icons-material';
import { NoMaxWidthTooltip } from '@centerpiece/components';
import { useAvaratCellContext } from './AvatarsCell.context';

const NO_NAME = 'No name';

export const getAlt = ({ contact_r, role_r }: AvatarProps): string => {
  return role_r && !contact_r
    ? role_r.name
    : `${contact_r?.firstname} ${contact_r?.lastname}`;
};

export const getTypography = ({ contact_r, role_r }: AvatarProps) => {
  return (
    <Typography
      sx={{
        fontSize: 12,
        paddingY: 0.5,
      }}
    >
      {role_r && !contact_r
        ? role_r.name
        : contact_r && role_r
        ? `${role_r.name} - ${contact_r?.firstname} ${contact_r?.lastname}`
        : contact_r
        ? `${contact_r?.firstname} ${contact_r?.lastname}`
        : role_r?.name || NO_NAME}
    </Typography>
  );
};

export const stringToColour = (str: string): string => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let colour = '#';

  for (let i = 0; i < 3; i++) {
    let value = (hash >> (i * 8)) & 0xff;

    colour += ('00' + value.toString(16)).slice(-2);
  }

  return colour;
};

export const getContent = (data: AvatarProps): string | null => {
  const name = getAlt(data);

  if (name === NO_NAME || !name) {
    return null;
  }

  let names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }

  return initials;
};

export interface AvatarProps extends MuiAvatarProps {
  thumbnail?: string;
  group?: string;
  tooltip?: any;
  contact_r?: {
    id: number;
    firstname: string;
    lastname: string;
    email: string;
  };
  role_r?: {
    id: number;
    name: string;
  };
  organization_r?: {
    id: number;
    name: string;
  };
}

export const Avatar = (avatarProps: AvatarProps) => {
  const { thumbnail, variant, ...rest } = avatarProps;
  const alt = getAlt(avatarProps);
  const bgcolor = stringToColour(alt || '');
  const content = getContent(avatarProps);
  const { sx, style, ...sxRest } = rest;
  const { zIndex: _zIndex, ...zIndexRest } = style || { zIndex: 1 };
  const [zIndex, setZIndex] = useState<any>((_zIndex || 1) as any);
  const { tooltip } = useAvaratCellContext();
  if (thumbnail) {
    return (
      <AvatarWrapper
        tooltip={
          (tooltip &&
            tooltip({
              ...(avatarProps.contact_r || {}),
              role_r: avatarProps.role_r,
              organization_r: avatarProps?.organization_r,
            })) ||
          getTypography(avatarProps)
        }
      >
        <MuiAvatar
          {...sxRest}
          alt={alt}
          src={thumbnail}
          sx={{
            ...zIndexRest,
            bgcolor,
            ...sx,
            zIndex,
            boxShadow: theme => theme.shadows[1],
          }}
          onMouseEnter={() => setZIndex(999)}
          onMouseLeave={() => setZIndex((_zIndex || 1) as any)}
          variant={variant}
        />
      </AvatarWrapper>
    );
  }
  if (!avatarProps?.contact_r?.firstname && !avatarProps?.role_r) {
    return <span />;
  } else if (content) {
    return (
      <AvatarWrapper
        tooltip={
          (tooltip &&
            tooltip({
              ...(avatarProps.contact_r || {}),
              role_r: avatarProps.role_r,
              organization_r: avatarProps?.organization_r,
            })) ||
          getTypography(avatarProps)
        }
      >
        <MuiAvatar
          {...sxRest}
          sx={{
            ...zIndexRest,
            bgcolor,
            ...sx,
            zIndex,
            boxShadow: theme => theme.shadows[3],
            borderColor: theme =>
              variant === 'square'
                ? `${theme.palette.primary.main} !important`
                : undefined,
            borderStyle: variant === 'square' ? 'outset !important' : undefined,
            // color: theme =>
            //   variant === 'square'
            //     ? `${theme.palette.primary.main} !important`
            //     : undefined,
            // fontWeight: variant === 'square' ? 'bold !important' : undefined,
          }}
          onMouseEnter={() => setZIndex(999)}
          onMouseLeave={() => setZIndex((_zIndex || 1) as any)}
          variant={variant}
        >
          {content}
        </MuiAvatar>
      </AvatarWrapper>
    );
  }

  return (
    <AvatarWrapper
      tooltip={
        (tooltip &&
          tooltip({
            ...(avatarProps.contact_r || {}),
            role_r: avatarProps.role_r,
            organization_r: avatarProps?.organization_r,
          })) ||
        getTypography(avatarProps)
      }
    >
      <MuiAvatar
        {...sxRest}
        sx={{
          ...zIndexRest,
          bgcolor,
          ...sx,
          zIndex,
          boxShadow: theme => theme.shadows[1],
        }}
        onMouseEnter={() => setZIndex(999)}
        onMouseLeave={() => setZIndex((_zIndex || 1) as any)}
        variant={variant}
      >
        <Person />
      </MuiAvatar>
    </AvatarWrapper>
  );
};

export interface AvatarWrapperProps {
  tooltip?: JSX.Element;
  children: any;
}

export const AvatarWrapper = ({ tooltip, children }: AvatarWrapperProps) => {
  return tooltip ? (
    <NoMaxWidthTooltip title={tooltip} arrow placement="top">
      {children}
    </NoMaxWidthTooltip>
  ) : (
    <>{children}</>
  );
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));
